import { type ChangeEvent, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { useTheme, Button, Divider, Box, Stack } from "@mui/material";
import { Page } from "@/components/Page";
import { BackLink } from "@/components/BackLink";
import { profileStore, snackStore } from "@/stores/Stores";
import { API_BASE_URL } from "@/common/config";
import { getAvatarUrl } from "@/util/avatarUtils";
import { formatUrl, useReferrer } from "@/hooks/useReferrer";
import { toFullLocale } from "@/util/languages";
import ActiveSessions from "./Sessions";
import ChangePasswordDialog from "./ChangePassword";
import TwoFactorAuthenticationDialog from "./TwoFactorAuthentication";
import TurnOff2FADialog from "./TurnOff2FA";
import DeleteAccountDialog from "./DeleteAccount";
import { BasicInfo } from "./BasicInfo";
import { AdditionalInfo } from "./AdditionalInfo";
import { NewsAndTerms } from "./NewsAndTerms";
import getStyles from "./styles";

export const Profile = observer(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = getStyles(theme);
  const referrer = useReferrer([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [show2FADialog, setShow2FADialog] = useState(false);
  const [showTurnOff2FADialog, setShowTurnOff2FADialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const { i18n } = useTranslation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    profileStore.setEditing(true);
  }, [profileStore]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    console.log("Profile Component Environment:", {
      API_BASE_URL,
      env: import.meta.env,
      profile: userProfile,
    });

    profileStore.fetchProfile().then(() => {
      i18n.changeLanguage(toFullLocale(profileStore.profile.locale));
      console.log("Debug - Profile component mounted", {
        apiBaseUrl: API_BASE_URL,
        avatarUrl: userProfile.avatarUrl,
        fullUrl: getAvatarUrl(userProfile.avatarUrl),
      });
    });

  }, [profileStore.token]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setShow2FADialog(profileStore.secret2FA !== null && profileStore.secret2FA !== "");
  }, [profileStore.secret2FA]);

  const userProfile = profileStore.profile;

  const handleCountryChange = (country: string) => {
    console.log("Country changed:", country);
    userProfile.country = country;
  };

  if (profileStore.profileMissing) {
    if (referrer === "") {
      snackStore.showError(`${t("Your profile is not complete")}. ${t("Please fill in all required fields")}.`);
    } else {
      snackStore.showError(`${t("Your profile is not complete")}. ${t("Please fill in all required fields then return to")} ${formatUrl(referrer)}.`);
    }
  }

  return (
    <>
      <Box sx={styles.page}>
        {referrer && <BackLink url={referrer} sx={{ padding: "1rem" }} />}
        <Page title={t("Your Profile")} sx={styles.page}>
          <Stack direction="column">
            <BasicInfo
              onSwitch2FA={(event: ChangeEvent<HTMLInputElement>) => {
                if (event.target.checked) {
                  profileStore.enable2FA();
                } else {
                  if (profileStore.profile.twoFactorAuth === "yes") {
                    setShowTurnOff2FADialog(true);
                  }
                }
              }}
              onChangePassword={() => setShowChangePasswordDialog(true)}
            />


            <AdditionalInfo onChangeCountry={handleCountryChange} />

            <NewsAndTerms />

            {profileStore.showErrors && userProfile.profileErrors?.length > 0 && (
              <Stack component="ul" sx={styles.error}>
                {userProfile.profileErrors.map((error) => (
                  <Box component="li" key={error}>
                    {error}
                  </Box>
                ))}
              </Stack>
            )}

            <Stack sx={{ ...styles.item, ...styles.actions }}>
              <Button
                onClick={() => {
                  if (profileStore.profile.isProfileFilled) {
                    profileStore.saveProfile().then(() => {
                      if (profileStore.saveStatus === "success") {
                        snackStore.showSuccess(t("Profile updated successfully"));
                        i18n.changeLanguage(toFullLocale(profileStore.profile.locale));
                      } else if (profileStore.saveStatus === "error") {
                        snackStore.showError(`${t("Profile update request was unsuccessful")}. ${t("Please try again")}.`);
                      }
                    });
                  } else {
                    profileStore.showErrors = true;
                  }
                }}
                variant="contained"
                color="primary"
                sx={styles.saveChanges}
              >
                {t("Save Changes")}
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ marginTop: "1.5rem", marginBottom: "1.5rem" }} />

          <ActiveSessions />

          {/* <Divider sx={{ marginTop: "1.5rem", marginBottom: "1.5rem" }} />

          <Stack sx={{ ...styles.item, ...styles.actions }}>
            <Button sx={styles.deleteAccount} onClick={() => setShowDeleteDialog(true)} color="error">
              {t("Delete Account")}
            </Button>
          </Stack> */}
        </Page>

        {referrer && <Box sx={{ height: "3rem" }} />}
      </Box>

      <ChangePasswordDialog
        open={showChangePasswordDialog}
        onCancel={() => setShowChangePasswordDialog(false)}
        onConfirm={(current_password, new_password) => {
          profileStore.passwordChange(current_password, new_password).then(() => {
            if (profileStore.passwordChangeStatus === "success") {
              snackStore.showSuccess(t("Password changed successfully"));
            } else if (profileStore.passwordChangeStatus === "error") {
              snackStore.showError(`${t("Password change request was unsuccessful")}. ${t("Please try again")}.`);
            }
          });
          setShowChangePasswordDialog(false);
        }}
      />

      <TwoFactorAuthenticationDialog
        open={show2FADialog}
        onCancel={() => setShow2FADialog(false)}
        onConfirm={() => {
          profileStore.verify2FA();
        }}
      />

      <TurnOff2FADialog
        open={showTurnOff2FADialog}
        onConfirm={() => {
          profileStore.disable2FA();
          setShowTurnOff2FADialog(false);
        }}
        onCancel={() => setShowTurnOff2FADialog(false)}
      />

      <DeleteAccountDialog
        open={showDeleteDialog}
        onConfirm={() => {
          profileStore.deleteAccount();
        }}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </>
  );
});
